import { FC, Fragment, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, List, ListItemButton, ListItemIcon, Popover, styled } from '@mui/material';

import { Button, ContentIcon } from '@components/base';
import useLayoutEntityFilterContext from '@hooks/useLayoutEntityFilter';
import { ContentType } from '@models/Content';

const StyledList = styled(List)(() => ({
  minWidth: 300,
  maxWidth: 1,
}));

const StyledListItemButton = styled(ListItemButton)(() => ({
  padding: 0,
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingRight: theme.spacing(),
}));

const TypeFilter: FC = () => {
  const { t } = useTranslation();
  const { typeValues } = useLayoutEntityFilterContext();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  if (!typeValues) return null;

  const { tKey, setTypes, types, typeOptions } = typeValues;

  if (typeOptions.length === 0) return null;

  const toggleType = (value: ContentType) =>
    types.includes(value)
      ? setTypes(types.filter(type => type !== value))
      : setTypes([...(types ?? []), value]);

  const open = Boolean(anchorEl);
  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <Fragment>
      <Button color='inherit' leftIcon='category' onClick={handleOpen} rightIcon='chevronDown'>
        {t(tKey)}
        {types.length > 0 ? ` (${types.length})` : ''}
      </Button>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        onClose={handleClose}
        open={open}
        slotProps={{ paper: { variant: 'elevation', elevation: 4 } }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        <StyledList>
          {typeOptions.map(type => (
            <StyledListItemButton divider key={type} onClick={() => toggleType(type)} tabIndex={-1}>
              <StyledListItemIcon>
                <Checkbox checked={types.includes(type)} />
                <ContentIcon contentType={type} fontSize='small' hideTooltip />
              </StyledListItemIcon>

              {t(`contentType.${type}`, { context: 'minified' })}
            </StyledListItemButton>
          ))}
        </StyledList>

        <Box component={Box} display='flex' justifyContent='flex-end' pb={1} px={1}>
          <Button disabled={types.length === 0} leftIcon='close' onClick={() => setTypes([])}>
            {t('clear')}
          </Button>
        </Box>
      </Popover>
    </Fragment>
  );
};

export default TypeFilter;
